import type { Channel } from 'stream-chat';

import type { StreamChatType } from '../types';

export type StableDatedChannel = {
  channel: Channel<StreamChatType>;
  hasDate: boolean;
  sortTime: number;
};

export type DatedChannelSort = 'activity-desc' | 'date-asc' | 'date-desc';

export const getRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

export const coercePositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const getChannelMetadata = (
  channel: Channel<StreamChatType>
): Record<string, unknown> => {
  const channelData = getRecord(channel.data);
  return getRecord(
    channelData.channelMetaData ??
      channelData.channelMetadata ??
      channelData.channel_meta_data
  );
};

const extractSessionIdFromText = (
  value: unknown,
  allowLooseSuffix = false
): number | null => {
  if (typeof value !== 'string' || !value.trim()) return null;

  const namedSessionMatch = value.match(
    /(?:cleaning[_-]?session|booking[_-]?session|session)[_-]?(\d+)/i
  );
  if (namedSessionMatch?.[1]) {
    return coercePositiveNumber(namedSessionMatch[1]);
  }

  if (!allowLooseSuffix) return null;

  const suffixMatch = value.match(/(?:^|[_:-])(\d+)\s*$/);
  return suffixMatch?.[1] ? coercePositiveNumber(suffixMatch[1]) : null;
};

export const resolveBookingSessionId = (
  channel: Channel<StreamChatType>
): number | null => {
  const channelData = getRecord(channel.data);
  const metadata = getChannelMetadata(channel);

  const directCandidates = [
    metadata.bookingSessionId,
    metadata.sessionId,
    metadata.booking_session_id,
    metadata.id,
    channelData.bookingSessionId,
    channelData.sessionId,
    channelData.booking_session_id,
    channelData.id,
  ];

  for (const candidate of directCandidates) {
    const parsed = coercePositiveNumber(candidate);
    if (parsed) return parsed;
  }

  const idCandidates = [channel.id, channel.cid, channelData.id];
  for (const candidate of idCandidates) {
    const parsed = extractSessionIdFromText(candidate, true);
    if (parsed) return parsed;
  }

  return extractSessionIdFromText(channelData.name, false);
};

export const parseDateCandidate = (value: unknown): Date | null => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const text = String(value).trim();

  if (text.includes('T')) {
    const date = new Date(text);
    if (!Number.isNaN(date.getTime())) return date;
  }

  const numericDateMatch = text.match(
    /^(\d{1,4})[/-](\d{1,2})[/-](\d{1,4})/
  );

  if (numericDateMatch) {
    const [, firstValue, secondValue, thirdValue] = numericDateMatch;
    const firstNumber = Number(firstValue);
    const secondNumber = Number(secondValue);
    const thirdNumber = Number(thirdValue);
    const isYearFirst = firstValue.length === 4;
    const year = isYearFirst ? firstNumber : thirdNumber;
    const month = secondNumber;
    const day = isYearFirst ? thirdNumber : firstNumber;
    const date = new Date(year, month - 1, day);

    if (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    ) {
      return date;
    }
  }

  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return null;

  return date;
};

const getLastMessage = (channel: Channel<StreamChatType>) => {
  const state = getRecord(
    (channel as unknown as Record<string, unknown>).state
  );
  const messages = Array.isArray(state.messages) ? state.messages : [];

  return getRecord(messages[messages.length - 1]);
};

const getTimestamp = (value: unknown): number => {
  const parsed = parseDateCandidate(value);
  return parsed ? parsed.getTime() : 0;
};

export const getChannelLastMessageTime = (
  channel: Channel<StreamChatType>
): number => {
  const channelData = getRecord(channel.data);
  const lastMessage = getLastMessage(channel);
  const candidates = [
    channelData.last_message_at,
    lastMessage.updated_at,
    lastMessage.created_at,
  ];

  for (const candidate of candidates) {
    const timestamp = getTimestamp(candidate);
    if (timestamp) return timestamp;
  }

  return 0;
};

export const getChannelUpdatedTime = (
  channel: Channel<StreamChatType>
): number => {
  const channelData = getRecord(channel.data);
  const lastMessage = getLastMessage(channel);
  const candidates = [
    channelData.updated_at,
    lastMessage.updated_at,
    lastMessage.created_at,
    channelData.created_at,
  ];

  for (const candidate of candidates) {
    const timestamp = getTimestamp(candidate);
    if (timestamp) return timestamp;
  }

  return 0;
};

export const getChannelActivityTime = (
  channel: Channel<StreamChatType>
): number => {
  const channelData = getRecord(channel.data);
  const lastMessage = getLastMessage(channel);
  const candidates = [
    channelData.last_message_at,
    channelData.updated_at,
    lastMessage.updated_at,
    lastMessage.created_at,
    channelData.created_at,
  ];

  for (const candidate of candidates) {
    const timestamp = getTimestamp(candidate);
    if (timestamp) return timestamp;
  }

  return 0;
};

const getChannelStableText = (channel: Channel<StreamChatType>): string => {
  const channelData = getRecord(channel.data);
  const candidates = [
    channel.cid,
    channel.id,
    channelData.id,
    channelData.name,
  ];

  for (const candidate of candidates) {
    const value = String(candidate ?? '').trim();
    if (value) return value;
  }

  return '';
};

export const compareChannelsByStableKey = (
  a: Channel<StreamChatType>,
  b: Channel<StreamChatType>
): number => {
  const aSessionId = resolveBookingSessionId(a);
  const bSessionId = resolveBookingSessionId(b);

  if (aSessionId && bSessionId && aSessionId !== bSessionId) {
    return aSessionId - bSessionId;
  }

  if (Boolean(aSessionId) !== Boolean(bSessionId)) {
    return aSessionId ? -1 : 1;
  }

  return getChannelStableText(a).localeCompare(
    getChannelStableText(b),
    undefined,
    {
      numeric: true,
      sensitivity: 'base',
    }
  );
};

export const compareChannelsByRecentActivity = (
  a: Channel<StreamChatType>,
  b: Channel<StreamChatType>
): number => {
  const lastMessageDiff =
    getChannelLastMessageTime(b) - getChannelLastMessageTime(a);

  if (lastMessageDiff) return lastMessageDiff;

  const updatedDiff = getChannelUpdatedTime(b) - getChannelUpdatedTime(a);

  if (updatedDiff) return updatedDiff;

  return compareChannelsByStableKey(a, b);
};

export const sortChannelsByRecentActivity = (
  channels: Channel<StreamChatType>[]
): Channel<StreamChatType>[] =>
  [...channels].sort(compareChannelsByRecentActivity);

export const sortDatedChannels = <T extends StableDatedChannel>(
  channels: T[],
  sort: DatedChannelSort
): T[] =>
  [...channels].sort((a, b) => {
    if (sort === 'activity-desc') {
      const activityDiff =
        getChannelActivityTime(b.channel) - getChannelActivityTime(a.channel);

      if (activityDiff) return activityDiff;

      return compareChannelsByStableKey(a.channel, b.channel);
    }

    if (a.hasDate !== b.hasDate) return a.hasDate ? -1 : 1;

    const dateDiff =
      sort === 'date-desc'
        ? b.sortTime - a.sortTime
        : a.sortTime - b.sortTime;

    if (dateDiff) return dateDiff;

    return compareChannelsByStableKey(a.channel, b.channel);
  });
